// askSmart-ui/src/pages/TOSP/portal/ExpenseTable.tsx
import React from "react";
import "./ExpenseTable.css";
import { useTospData } from "../TospDataContext";
import { Tenant } from "../types";
import { MONTH_NAMES } from "./portalSummary";
import {
  expenseYearFromSnapshot,
  publishedStatement,
  statementRatesFor,
  tenantExpenseYear,
} from "./tenantExpenses";
import {
  basisFromSnapshot,
  budgetStatement,
  camRecoveryLine,
  statementBasis,
} from "../budgetPublish";
import { projectRevenue } from "../revenueBudget";
import { changedHands, LeaseRent, splitStatement } from "../tenantStatement";
import { loadMyExpenses, PublishedExpenseYear } from "./tenantExpensesApi";
import { TenantSalesMonth } from "./tenantSales";

interface ExpenseTableProps {
  tenant: Tenant;
  sales: TenantSalesMonth[];
  /**
   * Who is looking, and it decides where the figures come from — not merely
   * which years are offered.
   *
   * A TENANT reads published statements, fetched from the server. Nothing is
   * worked out in the browser: the figures are the ones the admin published,
   * manual adjustments included, and the year list is whatever the server
   * returns. Both the gate and the arithmetic live on the other side.
   *
   * An ADMIN previews, and must be able to: every year the tenancy has run,
   * published or not. A published year shows the published figures, so the
   * preview and the tenant's screen agree; an unpublished one projects live,
   * which is what the preview is for.
   *
   * Defaults to the tenant, so a screen that forgets to say shows less rather
   * than more.
   */
  audience?: "admin" | "tenant";
}

// Cents, not whole dollars: this is what will be taken from the account, so it
// is shown exactly even though the sales screens round.
const money = (value: number | null): string =>
  value === null
    ? "—"
    : `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

// What a tenant is invoiced, month by month, a year at a time.
//
// Shared between the tenant's own Expenses screen and the admin's Tenant
// Details modal — one table, so a tenant querying a figure and the property
// manager answering are looking at the same arithmetic. It brings its own
// stylesheet for that reason: the two screens load different ones.
export default function ExpenseTable({ tenant, sales, audience = "tenant" }: ExpenseTableProps) {
  const { unitById, units, tenants, unitLabel, configuration, taxRolls, budgets } =
    useTospData();

  // What the server has released to this tenant. Fetched for the tenant only:
  // an admin is previewing and reads the budgets it already holds.
  const [published, setPublished] = React.useState<PublishedExpenseYear[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (audience === "admin") return;
    let live = true;
    loadMyExpenses()
      .then((years) => live && setPublished(years))
      .catch((err) => live && setLoadError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [audience]);

  // Newest first, so the year being lived in is the one already open.
  //
  // BOTH AUDIENCES SEE BUDGET YEARS. Nothing on this screen comes from a lease
  // date any more — a statement is the budget read tenancy by tenancy, so a
  // year with no budget has nothing to show and should not offer a tab.
  //
  // The difference between them is WHICH budget years, not where the years
  // come from. An admin sees every budget in the org, published or not, because
  // the point of the preview is to check what a tenant will see before they can
  // see it. A tenant sees only what the server released to them, and no
  // client-side rule decides it.
  //
  // Years used to be worked out from the lease commencements, the reported
  // sales and the tax rolls, with the budget list only ever ADDING to them.
  // That is why a tenancy showed 2025 and 2026 while one budget existed and
  // neither year had been agreed.
  const years = React.useMemo(
    () =>
      audience === "admin"
        ? [...new Set(budgets.map((budget) => budget.year))].sort((a, b) => b - a)
        : (published || []).map((entry) => entry.year),
    [audience, budgets, published],
  );
  const [year, setYear] = React.useState(years[0]);

  // A tenant switch, or the fetch landing, can leave the chosen year outside
  // the new list.
  React.useEffect(() => {
    if (!years.includes(year)) setYear(years[0]);
  }, [years, year]);

  const shownYear = years.includes(year) ? year : years[0];

  // The year's whole revenue budget, worked out once rather than once per
  // tenancy — it divides eleven expense categories across ninety-odd units and
  // projects every lease in the portfolio, which is far too much to repeat for
  // one statement. Admin only: a tenant reads a frozen statement, and this
  // needs every unit and every tenancy, which the portal isn't given.
  const liveBudget = React.useMemo(() => {
    if (audience !== "admin" || shownYear === undefined) return null;
    const budget = budgets.find((candidate) => candidate.year === shownYear);
    if (!budget) return null;
    const projection = projectRevenue(
      shownYear,
      units,
      tenants,
      unitLabel,
      configuration,
      taxRolls,
      sales,
      budget.camOccupancy,
    );
    const camRecovery = camRecoveryLine(budget, units, tenants, budget.expenseOverrides);
    return {
      budget,
      projected: projection.lines,
      camRecovery,
      // Carries the per-unit recoveries, for a space that changed hands.
      basis: statementBasis({ budget, projection, camRecovery, units, tenants }),
    };
  }, [
    audience,
    shownYear,
    budgets,
    units,
    tenants,
    unitLabel,
    configuration,
    taxRolls,
    sales,
  ]);

  // Rent as this tenancy's own lease states it — for an arrival the budget
  // never saw, who has no row to read. Only the two rent lines are taken from
  // the live calculator; every recovery on the statement comes from the budget.
  const leaseRentFor = (who: Tenant, forYear: number): LeaseRent => {
    const live = tenantExpenseYear({
      tenant: who,
      year: forYear,
      unitById,
      configuration,
      taxRolls,
      sales: [],
    });
    const of = (key: string): number[] =>
      (live.lines.find((line) => line.key === key)?.months || new Array(12).fill(0)).map(
        (amount) => amount ?? 0,
      );
    return { baseRent: of("baseRent"), storageRent: of("storageRent") };
  };

  const statement = React.useMemo(() => {
    // Falls back to the current year only to keep the hook's shape stable
    // where there are no years at all — nothing is rendered from it, since the
    // empty state below returns first.
    const forYear = shownYear ?? new Date().getFullYear();

    if (audience === "admin") {
      // A published year reads its frozen statement, so the preview and the
      // tenant's own screen cannot disagree.
      const frozen = publishedStatement(budgets, tenant.id, forYear);
      if (frozen) return expenseYearFromSnapshot(frozen, forYear);

      // AN UNPUBLISHED YEAR IS THE BUDGET, READ TENANCY BY TENANCY — through
      // the very function that will build the published statement, so the
      // preview is what publishing would release rather than a second opinion
      // worked out from the leases. Nothing to preview without a budget.
      if (!liveBudget) return null;
      const rates = statementRatesFor(tenant, unitById, taxRolls, forYear);

      // A SPACE THAT CHANGED HANDS IS DIVIDED. The budget still charges whoever
      // held it when the year opened; the two tenancies' statements split those
      // figures by the months each of them held it, and the year end settles
      // the difference. Everybody else reads their own budget row, which keeps
      // whatever was keyed on the revenue sheet.
      const divided = changedHands(tenant, forYear, units)
        ? splitStatement({
            tenant,
            basis: liveBudget.basis,
            units,
            // An arrival the budget never saw takes their rent from their own
            // lease, which is what the live calculator reads.
            leaseRent: leaseRentFor(tenant, forYear),
            rates,
          })
        : budgetStatement({
            tenant,
            budget: liveBudget.budget,
            projected: liveBudget.projected,
            camRecovery: liveBudget.camRecovery,
            rates,
          });
      return divided ? expenseYearFromSnapshot(divided, forYear) : null;
    }

    // THE TENANT PATH NEVER PROJECTS. Every figure here was published by the
    // admin; nothing is worked out from leases and rates.
    //
    // It does DIVIDE, where a space of theirs changed hands inside the year:
    // the frozen per-unit figures, apportioned by the months this tenancy held
    // each unit. That is not a recalculation — the money is the published
    // money — it is the one thing publication could not have known, because
    // the handover was set up afterwards.
    const year = (published || []).find((entry) => entry.year === forYear);
    const mine = year?.statements.find((row) => row.tenantId === tenant.id);
    if (year?.perUnit && changedHands(tenant, forYear, units)) {
      const basis = basisFromSnapshot(
        { perUnit: year.perUnit, revenue: [] },
        forYear,
      );
      const split =
        basis &&
        splitStatement({
          tenant,
          basis,
          units,
          leaseRent: leaseRentFor(tenant, forYear),
          rates: mine
            ? { sqFt: mine.perSqFt.sqFt, taxes: mine.perSqFt.taxes }
            : statementRatesFor(tenant, unitById, taxRolls, forYear),
        });
      if (split) return expenseYearFromSnapshot(split, forYear);
    }
    return mine ? expenseYearFromSnapshot(mine, forYear) : null;
  }, [
    audience,
    published,
    budgets,
    tenant,
    shownYear,
    liveBudget,
    unitById,
    configuration,
    taxRolls,
    sales,
  ]);

  // Two decimals, the way a rate is quoted rather than the way a total is.
  const psf = (value: number | null): string =>
    value === null ? "—" : `$${value.toFixed(2)}`;

  if (loadError) {
    return (
      <p className="tosp-portal-sheet-empty" role="alert">
        {loadError}
      </p>
    );
  }

  // Still fetching. Distinguished from "nothing released" so a slow response
  // doesn't read as an answer.
  if (audience === "tenant" && published === null) {
    return <p className="tosp-portal-sheet-empty">Loading your expenses…</p>;
  }

  // Nothing to show. Said plainly rather than drawn as a sheet of dashes,
  // which would read as charges nobody has worked out rather than as a
  // statement that doesn't exist.
  //
  // An admin reaches this for a tenancy that carries nothing on the year's
  // budget — one that took a space over part-way through the year, since the
  // budget charges whoever held it when the year opened. That is the budget
  // being right, not a statement being missing, so it says which it is.
  if (years.length === 0 || !statement) {
    return (
      <p className="tosp-portal-sheet-empty">
        {audience === "admin" && years.length > 0
          ? `This tenancy carries no charges on the ${shownYear} budget, so there is ` +
            `nothing to preview. A tenancy that took its space over during the year ` +
            `is not on that year's budget — the year was charged to whoever held it ` +
            `when it opened.`
          : `No expense statement is available yet. They appear here once the year's ` +
            `budget has been finalised and released.`}
      </p>
    );
  }

  return (
    <>
      {/* The headline rates, above everything: the two figures asked for on the
          phone, where the table below is what gets checked line by line. They
          follow the year selected under them. */}
      <div className="tosp-statement-rates">
        <div className="tosp-statement-rate">
          <span className="tosp-statement-rate-label">CAMS psf</span>
          <span className="tosp-statement-rate-value">{psf(statement.perSqFt.cams)}</span>
        </div>
        {/* One rate per unit, never blended: commercial space and a locker are
            assessed on different bases, so averaging them reports both wrong.
            A tenant holding one unit sees one figure, as before. */}
        <div className="tosp-statement-rate">
          <span className="tosp-statement-rate-label">Taxes psf</span>
          {statement.perSqFt.taxes.length === 0 ? (
            <span className="tosp-statement-rate-value">—</span>
          ) : statement.perSqFt.taxes.length === 1 ? (
            <span className="tosp-statement-rate-value">
              {psf(statement.perSqFt.taxes[0].psf)}
            </span>
          ) : (
            <span className="tosp-statement-rate-units">
              {statement.perSqFt.taxes.map((rate) => (
                <span key={rate.unitId} className="tosp-statement-rate-unit">
                  <span className="tosp-statement-rate-unit-name">{rate.label}</span>
                  <span className="tosp-statement-rate-unit-value">{psf(rate.psf)}</span>
                </span>
              ))}
            </span>
          )}
        </div>
      </div>

      <div className="tosp-statement-years" role="tablist" aria-label="Statement year">
        {years.map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={option === statement.year}
            className={`tosp-statement-year${
              option === statement.year ? " tosp-statement-year-on" : ""
            }`}
            onClick={() => setYear(option)}
          >
            {option}
          </button>
        ))}
      </div>

      {/* Wide by nature — thirteen money columns — so it scrolls sideways
          inside its own frame rather than pushing the page out. */}
      <div className="tosp-statement-scroll">
        <table className="tosp-statement">
          <thead>
            <tr>
              <th scope="col" className="tosp-statement-item">
                Item
              </th>
              {MONTH_NAMES.map((name) => (
                <th key={name} scope="col">
                  {name}
                </th>
              ))}
              <th scope="col" className="tosp-statement-sum">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {statement.lines.map((line) => (
              <tr
                key={line.key}
                className={line.emphasis ? "tosp-statement-total" : undefined}
              >
                <th scope="row" className="tosp-statement-item">
                  {line.label}
                </th>
                {line.months.map((amount, index) => (
                  <td key={index}>{money(amount)}</td>
                ))}
                <td className="tosp-statement-sum">{money(line.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </>
  );
}
